import { Node, Edge } from '@xyflow/react';
import {
  EDataType,
  EDataSwitchType,
  NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';

export const getCategoryName = (node: Node): string =>
  (node.data?.category as NodeCategoryModel)?.name?.trim() ?? '';

export const sortNodesByName = (
  a: Node<Record<string, unknown>>,
  b: Node<Record<string, unknown>>,
) =>
  getCategoryName(a).localeCompare(getCategoryName(b), undefined, {
    numeric: true,
  });

/** Build groups (switch + its devices) from ordered nodes, sorted by switch name. */
export const buildDisplayGroups = (
  orderedNodes: Node<Record<string, unknown>>[],
): Node<Record<string, unknown>>[][] => {
  const filtered = orderedNodes.filter(
    (n) =>
      (n.data?.category as NodeCategoryModel)?.type === EDataType.Device ||
      (n.data?.category as NodeCategoryModel)?.type === EDataType.Switch,
  );
  const groups: Node<Record<string, unknown>>[][] = [];
  let current: Node<Record<string, unknown>>[] = [];
  filtered.forEach((node) => {
    const isSwitch =
      (node.data?.category as NodeCategoryModel)?.type === EDataType.Switch;
    if (isSwitch) {
      if (current.length > 0) groups.push(current);
      current = [node];
    } else {
      current.push(node);
    }
  });
  if (current.length > 0) groups.push(current);
  const sortByFirstNodeName = (
    a: Node<Record<string, unknown>>[],
    b: Node<Record<string, unknown>>[],
  ) =>
    getCategoryName(a[0]).localeCompare(getCategoryName(b[0]), undefined, {
      numeric: true,
    });
  groups.sort(sortByFirstNodeName);
  return groups;
};

/** Flatten groups: switch first, then devices sorted by name within each group. */
export const flattenGroupsWithDevicesSorted = (
  groups: Node<Record<string, unknown>>[][],
): Node<Record<string, unknown>>[] => {
  const result: Node<Record<string, unknown>>[] = [];
  groups.forEach((group) => {
    const isSwitchGroup =
      (group[0]?.data?.category as NodeCategoryModel)?.type ===
      EDataType.Switch;
    if (isSwitchGroup) {
      const [head, ...rest] = group;
      result.push(head);
      rest.sort(sortNodesByName);
      result.push(...rest);
    } else {
      group.sort(sortNodesByName);
      result.push(...group);
    }
  });
  return result;
};

interface NodeGroup {
  switch: Node<Record<string, unknown>>;
  connectedDevices: Node<Record<string, unknown>>[];
}

export const orderNodes = (
  nodes: Node<Record<string, unknown>>[],
  edges: Edge[],
) => {
  const nodeGroups: NodeGroup[] = [];

  // Find all Switches
  const switches = nodes.filter(
    (node) =>
      (node.data?.category as NodeCategoryModel)?.type === EDataType.Switch,
    EDataType.Switch,
  );

  // Create groups for each Switch
  switches.forEach((switchNode) => {
    const connectedDevices: Node<Record<string, unknown>>[] = [];

    // Find all devices connected directly to the Switch
    edges.forEach((edge) => {
      if (edge.source === switchNode.id) {
        const connectedNode = nodes.find((n) => n.id === edge.target);
        if (
          connectedNode &&
          (connectedNode.data as { category: { type: EDataType } }).category
            .type === EDataType.Device
        ) {
          connectedDevices.push(connectedNode);
        }
      } else if (edge.target === switchNode.id) {
        const connectedNode = nodes.find((n) => n.id === edge.source);
        if (
          connectedNode &&
          (connectedNode.data as { category: { type: EDataType } }).category
            .type === EDataType.Device
        ) {
          connectedDevices.push(connectedNode);
        }
      }
    });

    nodeGroups.push({
      switch: switchNode,
      connectedDevices,
    });
  });

  // Sort the groups by the number of connected devices
  nodeGroups.sort(
    (a, b) => b.connectedDevices.length - a.connectedDevices.length,
  );
  // Create the sorted result array
  const orderedNodes: Node<Record<string, unknown>>[] = [];

  nodeGroups.forEach((group) => {
    orderedNodes.push(group.switch);
    orderedNodes.push(...group.connectedDevices);
  });

  // Add the remaining nodes that haven't been processed
  const remainingNodes = nodes.filter((node) => !orderedNodes.includes(node));

  return {
    orderedNodes,
    remainingNodes,
  };
};

const sortByName = (a: Node, b: Node) => {
  const nameA = (a.data?.category as NodeCategoryModel)?.name?.trim() ?? '';
  const nameB = (b.data?.category as NodeCategoryModel)?.name?.trim() ?? '';
  return nameA.localeCompare(nameB, undefined, { numeric: true });
};

const isSwitchNode = (node: Node) =>
  (node.data?.category as NodeCategoryModel)?.type === EDataType.Switch;

export const filterCanSettingNode = (node: Node) => {
  const type = (node.data?.category as NodeCategoryModel)?.type;
  const isManagedSwitch = (
    node.data?.category as NodeCategoryModel
  )?.switchType?.includes(EDataSwitchType.MANAGED);
  return (
    type === EDataType.Device || (type === EDataType.Switch && isManagedSwitch)
  );
};

export const groupBySwitch = (nodes: Node[]) => {
  const groups: Node[][] = [];
  let currentGroup: Node[] = [];

  nodes.forEach((node) => {
    if (isSwitchNode(node)) {
      if (currentGroup.length > 0) groups.push(currentGroup);
      currentGroup = [node];
    } else {
      currentGroup.push(node);
    }
  });

  if (currentGroup.length > 0) groups.push(currentGroup);

  return groups;
};

export const sortGroupsBySwitchName = (groups: Node[][]) => {
  const bySwitchName = (a: Node[], b: Node[]) => {
    const nameA =
      (a[0]?.data?.category as NodeCategoryModel)?.name?.trim() ?? '';
    const nameB =
      (b[0]?.data?.category as NodeCategoryModel)?.name?.trim() ?? '';
    return nameA.localeCompare(nameB, undefined, { numeric: true });
  };

  return [...groups].sort(bySwitchName);
};

export const sortDevicesWithinGroups = (groups: Node[][]) => {
  const result: Node[] = [];

  groups.forEach((group) => {
    if (!group.length) return;

    const isSwitchGroup = isSwitchNode(group[0]);

    if (isSwitchGroup) {
      const [head, ...rest] = group;
      result.push(head);
      rest.sort(sortByName);
      result.push(...rest);
    } else {
      group.sort(sortByName);
      result.push(...group);
    }
  });

  return result;
};
